import type { Me, AskRequest, AskResponse, SavingsInitiative, Contract, Supplier, Category, SpendLine, Stakeholder, CreditBalanceView, FeedbackRequest, LegalDocument, TermsAcceptance } from '@procuai/contracts';

/** Thin client for the ProcuAI API. Sends the session cookie (credentials: include). */
export class ApiError extends Error {
  constructor(public status: number, public code: string, message: string) {
    super(message);
  }
}

export async function api<T>(path: string, init?: RequestInit): Promise<T> {
  // Only declare a JSON body when one is actually sent: a bodyless request (GET, DELETE, the
  // no-payload POSTs like demo seed/wipe and logout) with content-type application/json is rejected
  // by Fastify ("Body cannot be empty when content-type is set to 'application/json'").
  const hasBody = init?.body != null;
  const res = await fetch(`/api/v1${path}`, {
    credentials: 'include',
    ...init,
    headers: {
      ...(hasBody ? { 'content-type': 'application/json' } : {}),
      ...(init?.headers as Record<string, string> | undefined),
    },
  });
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  const body = text ? (JSON.parse(text) as unknown) : undefined;
  if (!res.ok) {
    const err = (body as { error?: { code?: string; message?: string } })?.error;
    throw new ApiError(res.status, err?.code ?? 'error', err?.message ?? res.statusText);
  }
  return body as T;
}

export const getMe = () => api<Me>('/me');
export const acceptTerms = (version: string) => api<void>('/me/accept-terms', { method: 'POST', body: JSON.stringify({ version }) });
// Legal documents store + acceptance archive (BP-22): the gate text, the Settings archive, the public pages.
export const getTermsAcceptances = () => api<TermsAcceptance[]>('/me/terms-acceptances');
export const getLegalDocument = (docType: string, version: string) => api<LegalDocument>(`/legal-documents/${docType}/${version}`);
export const getPublicLegalDocument = (docType: string) => api<LegalDocument>(`/legal/${docType}`);
export const requestLink = (email: string) => api<{ ok: boolean }>('/auth/signup-link', { method: 'POST', body: JSON.stringify({ email }) });
export const consumeLink = (token: string) =>
  api<{ status: 'ok' | 'totp_required' | 'choose_org'; orgs?: Array<{ id: string; name: string }> }>(
    '/auth/magic-link/consume',
    { method: 'POST', body: JSON.stringify({ token }) },
  );
export const askOrchestrator = (req: AskRequest) =>
  api<AskResponse>('/orchestrator/ask', { method: 'POST', body: JSON.stringify(req) });
export const getCredits = () => api<CreditBalanceView>('/orchestrator/credits');
export const sendFeedback = (input: FeedbackRequest) =>
  api<void>('/orchestrator/feedback', { method: 'POST', body: JSON.stringify(input) });
// Confirmed drafted writes go through the module's existing audited endpoints (AI prepares, human confirms).
export const createSavingsInitiative = (payload: Record<string, unknown>) =>
  api<SavingsInitiative>('/savings/initiatives', { method: 'POST', body: JSON.stringify(payload) });
export const deleteSavingsInitiative = (id: string) =>
  api<void>(`/savings/initiatives/${id}`, { method: 'DELETE' });
export const createContract = (payload: Record<string, unknown>) =>
  api<Contract>('/contracts', { method: 'POST', body: JSON.stringify(payload) });
export const deleteContract = (id: string) => api<void>(`/contracts/${id}`, { method: 'DELETE' });
export const createSupplier = (payload: Record<string, unknown>) =>
  api<Supplier>('/master-data/suppliers', { method: 'POST', body: JSON.stringify(payload) });
export const deleteSupplier = (id: string) => api<void>(`/master-data/suppliers/${id}`, { method: 'DELETE' });
export const createCategory = (payload: Record<string, unknown>) =>
  api<Category>('/master-data/categories', { method: 'POST', body: JSON.stringify(payload) });
export const deleteCategory = (id: string) => api<void>(`/master-data/categories/${id}`, { method: 'DELETE' });
export const createSpendLine = (payload: Record<string, unknown>) =>
  api<SpendLine>('/spend/lines', { method: 'POST', body: JSON.stringify(payload) });
export const deleteSpendLine = (id: string) => api<void>(`/spend/lines/${id}`, { method: 'DELETE' });
export const createStakeholder = (payload: Record<string, unknown>) =>
  api<Stakeholder>('/stakeholders', { method: 'POST', body: JSON.stringify(payload) });
export const deleteStakeholder = (id: string) => api<void>(`/stakeholders/${id}`, { method: 'DELETE' });
export const seedDemoData = () => api<{ seeded: number }>('/demo/seed', { method: 'POST' });
export const wipeDemoData = () => api<{ wiped: number }>('/demo/wipe', { method: 'POST' });
export const logout = () => api<void>('/auth/logout', { method: 'POST' });
export const stopImpersonation = () => api<void>('/impersonation/stop', { method: 'POST' });
